import { useEffect, useState } from "react";
import {
  X,
  Sparkles,
  Save,
  FileText,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Ruler,
} from "lucide-react";
import {
  useFormatConstraints,
  useSuggestFormatConstraints,
  useUpdateFormatConstraints,
  usePageReport,
  useTrimPlan,
  type FormatConstraintUpdate,
  type PageReport,
  type TrimPlan,
  type TrimSuggestion,
} from "@/hooks/use-format";
import { useResumeSection } from "@/hooks/use-proposals";
import { Scissors, Check } from "lucide-react";

interface FormatPageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  licitacionId: string;
  proposalId: string;
}

const EMPTY: FormatConstraintUpdate = {
  max_pages: null,
  pages_include_annexes: true,
  excess_not_scored: false,
  font_family: null,
  font_size_pt: null,
  line_spacing: null,
  margin_cm: null,
  source_quote: null,
};

export function FormatPageDialog({
  isOpen,
  onClose,
  licitacionId,
  proposalId,
}: FormatPageDialogProps) {
  const { data: constraint } = useFormatConstraints(
    isOpen ? licitacionId : undefined
  );
  const suggest = useSuggestFormatConstraints(licitacionId);
  const update = useUpdateFormatConstraints(licitacionId);
  const report = usePageReport(proposalId);

  const [form, setForm] = useState<FormatConstraintUpdate>(EMPTY);
  const [lastReport, setLastReport] = useState<PageReport | null>(null);

  // Cargar el formulario cuando llegan/actualizan los límites.
  useEffect(() => {
    if (constraint) {
      setForm({
        max_pages: constraint.max_pages ?? null,
        pages_include_annexes: constraint.pages_include_annexes ?? true,
        excess_not_scored: constraint.excess_not_scored ?? false,
        font_family: constraint.font_family ?? null,
        font_size_pt: constraint.font_size_pt ?? null,
        line_spacing: constraint.line_spacing ?? null,
        margin_cm: constraint.margin_cm ?? null,
        source_quote: constraint.source_quote ?? null,
      });
    }
  }, [constraint]);

  if (!isOpen) return null;

  const num = (v: string): number | null => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  const set = (patch: Partial<FormatConstraintUpdate>) =>
    setForm((f) => ({ ...f, ...patch }));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Formato y páginas"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Ruler className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Formato y páginas
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Límites del pliego */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">
                Límites del pliego
              </h3>
              <button
                type="button"
                onClick={() => suggest.mutate()}
                disabled={suggest.isPending}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 disabled:opacity-60"
              >
                {suggest.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                Extraer del pliego
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Máx. páginas</span>
                <input
                  type="number"
                  min={1}
                  value={form.max_pages ?? ""}
                  onChange={(e) => set({ max_pages: num(e.target.value) })}
                  className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Tipo de letra</span>
                <input
                  type="text"
                  value={form.font_family ?? ""}
                  placeholder="Source Sans Pro"
                  onChange={(e) => set({ font_family: e.target.value || null })}
                  className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Tamaño (pt)</span>
                <input
                  type="number"
                  step="0.5"
                  value={form.font_size_pt ?? ""}
                  onChange={(e) => set({ font_size_pt: num(e.target.value) })}
                  className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Interlineado</span>
                <input
                  type="number"
                  step="0.05"
                  value={form.line_spacing ?? ""}
                  placeholder="1.5"
                  onChange={(e) => set({ line_spacing: num(e.target.value) })}
                  className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Margen (cm)</span>
                <input
                  type="number"
                  step="0.5"
                  value={form.margin_cm ?? ""}
                  onChange={(e) => set({ margin_cm: num(e.target.value) })}
                  className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-600">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={!!form.pages_include_annexes}
                  onChange={(e) =>
                    set({ pages_include_annexes: e.target.checked })
                  }
                />
                El tope incluye los anexos
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={!!form.excess_not_scored}
                  onChange={(e) => set({ excess_not_scored: e.target.checked })}
                />
                El exceso no se valora
              </label>
            </div>

            {form.source_quote && (
              <p className="mt-2 text-[11px] text-gray-500 italic border-l-2 border-gray-200 pl-2">
                “{form.source_quote}”
              </p>
            )}

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => update.mutate(form)}
                disabled={update.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60"
              >
                {update.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar límites
              </button>
              {constraint?.notice && (
                <span className="text-[11px] text-amber-700">
                  {constraint.notice}
                </span>
              )}
            </div>
          </section>

          {/* Recuento de páginas */}
          <section className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">
                Recuento de páginas
              </h3>
              <button
                type="button"
                onClick={() =>
                  report.mutate(undefined, {
                    onSuccess: (r) => setLastReport(r),
                  })
                }
                disabled={report.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-60"
              >
                {report.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                Comprobar páginas
              </button>
            </div>

            {lastReport && <PageReportView report={lastReport} />}
            {!lastReport && (
              <p className="text-xs text-gray-500">
                Pulsa “Comprobar páginas” para contar las páginas reales de la
                propuesta con el formato exigido por el pliego.
              </p>
            )}
          </section>

          {/* Asistente de recorte — solo si te pasas del límite */}
          {lastReport?.is_over && (
            <TrimSection proposalId={proposalId} />
          )}
        </div>

        <div className="flex justify-end px-6 py-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function PageReportView({ report }: { report: PageReport }) {
  const over = report.is_over;
  const beyondIds = new Set(
    (report.beyond_limit_sections ?? []).map((s) => s.section_id)
  );

  return (
    <div className="space-y-3">
      {/* Resumen */}
      <div
        className={`flex items-center gap-2 p-3 rounded-md border text-sm ${
          over
            ? "bg-red-50 border-red-200 text-red-800"
            : "bg-emerald-50 border-emerald-200 text-emerald-800"
        }`}
      >
        {over ? (
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        ) : (
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        )}
        <span>
          <strong>{report.counted_pages}</strong>
          {report.max_pages ? ` / ${report.max_pages}` : ""} páginas
          {over ? (
            <>
              {" "}
              — te pasas por <strong>{report.over_by}</strong>.{" "}
              {report.excess_not_scored
                ? "El exceso no se valorará."
                : "Recorta antes de presentar."}
            </>
          ) : report.max_pages ? (
            " — dentro del límite."
          ) : (
            " (el pliego no fija un tope de páginas)."
          )}
        </span>
      </div>

      {/* Qué queda fuera del límite */}
      {over && report.beyond_limit_sections.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-red-700 mb-1">
            Secciones que caen fuera del límite (no se valorarán):
          </p>
          <ul className="text-xs text-gray-700 space-y-0.5">
            {report.beyond_limit_sections.map((s) => (
              <li key={s.section_id}>
                • {s.title}{" "}
                <span className="text-gray-400">(desde pág. {s.start_page})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Desglose por sección */}
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-1">
          Páginas por sección:
        </p>
        <ul className="text-xs divide-y divide-gray-50 border border-gray-100 rounded-md">
          {report.sections.map((s) => (
            <li
              key={s.section_id}
              className={`flex items-center justify-between px-2 py-1 ${
                beyondIds.has(s.section_id) ? "bg-red-50/50" : ""
              }`}
            >
              <span className="truncate text-gray-700">{s.title}</span>
              <span className="text-gray-400 flex-shrink-0 ml-2">
                pág. {s.start_page}
                {s.end_page !== s.start_page ? `–${s.end_page}` : ""} ·{" "}
                {s.pages} p.
              </span>
            </li>
          ))}
        </ul>
      </div>

      {report.notice && (
        <p className="text-[11px] text-gray-400">{report.notice}</p>
      )}
      {!report.exact && (
        <p className="text-[11px] text-amber-600">
          Estimación aproximada. El recuento exacto se calcula al renderizar en
          el servidor.
        </p>
      )}
    </div>
  );
}

function TrimSection({ proposalId }: { proposalId: string }) {
  const trim = useTrimPlan(proposalId);
  const resume = useResumeSection(proposalId);
  const [plan, setPlan] = useState<TrimPlan | null>(null);
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());
  const [applyingId, setApplyingId] = useState<string | null>(null);

  const toCut = (plan?.suggestions ?? []).filter(
    (s) => s.action === "condensar" && s.target_words
  );

  const apply = (s: TrimSuggestion) => {
    if (!s.target_words) return;
    setApplyingId(s.section_id);
    resume.mutate(
      {
        sectionId: s.section_id,
        target_words: s.target_words,
        // Pasar el "qué recortar" que propuso el asistente para que el resumen
        // recorte exactamente eso (no de forma genérica).
        instructions: s.rationale || undefined,
      },
      {
        onSuccess: () => {
          setAppliedIds((prev) => new Set(prev).add(s.section_id));
          setApplyingId(null);
        },
        onSettled: () => setApplyingId(null),
      }
    );
  };

  return (
    <section className="border-t border-gray-100 pt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
          <Scissors className="w-4 h-4 text-gray-500" />
          Recortar para caber
        </h3>
        <button
          type="button"
          onClick={() => trim.mutate(undefined, { onSuccess: (p) => setPlan(p) })}
          disabled={trim.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-60"
        >
          {trim.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          Sugerir recortes
        </button>
      </div>

      {!plan && (
        <p className="text-xs text-gray-500">
          La IA propone qué secciones condensar para caber en el límite,
          protegiendo lo que se puntúa. Aplica cada recorte con un clic.
        </p>
      )}

      {plan && (
        <div className="space-y-2">
          {plan.summary && (
            <p className="text-xs text-gray-700">{plan.summary}</p>
          )}
          {toCut.length === 0 && (
            <p className="text-xs text-gray-500">
              La IA no encontró recortes claros; revisa las secciones más largas a
              mano.
            </p>
          )}
          <ul className="space-y-1.5">
            {toCut.map((s) => {
              const done = appliedIds.has(s.section_id);
              return (
                <li
                  key={s.section_id}
                  className="border border-gray-200 rounded-md p-2 text-xs"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className="font-medium text-gray-800">
                        {s.title}
                      </span>
                      <span className="text-gray-500">
                        {" "}
                        · {s.current_words} → {s.target_words} palabras · ahorra
                        ~{s.pages_saved_est} pág.
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => apply(s)}
                      disabled={done || applyingId === s.section_id}
                      className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium ${
                        done
                          ? "text-emerald-700 bg-emerald-50"
                          : "text-white bg-purple-600 hover:bg-purple-700"
                      } disabled:opacity-60`}
                    >
                      {applyingId === s.section_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : done ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Scissors className="w-3 h-3" />
                      )}
                      {done ? "Aplicado" : "Aplicar"}
                    </button>
                  </div>
                  {s.rationale && (
                    <p className="text-gray-500 mt-0.5">{s.rationale}</p>
                  )}
                </li>
              );
            })}
          </ul>
          {appliedIds.size > 0 && (
            <p className="text-[11px] text-gray-500">
              Tras aplicar recortes, vuelve a pulsar “Comprobar páginas” para ver
              el nuevo total.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

export default FormatPageDialog;
